import { useEffect, useRef, useState } from 'react';
import SearchableSelect from 'components/SearchableSelect';
import { DECISIONES } from './decisiones';
import styles from './QuestionFormModal.module.css';

const MAX_QUESTION_TEXT = 2000;

const parseOptions = (str) => {
  if (!str) return [];
  if (Array.isArray(str)) return str;
  try { const p = JSON.parse(str); if (Array.isArray(p)) return p; } catch {}
  return String(str).split(',').map(s => s.trim()).filter(Boolean);
};
const serializeOptions = (arr) => arr.join(', ');

function OptionsEditor({ options, onChange, maxChoices, onMaxChoicesChange, isMultiple }) {
  const [newOpt, setNewOpt] = useState('');
  const add = () => {
    const val = newOpt.trim();
    if (!val || options.includes(val)) return;
    onChange([...options, val]); setNewOpt('');
  };
  const remove = (i) => onChange(options.filter((_, idx) => idx !== i));
  const handleKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } };

  return (
    <div className={styles.editor}>
      <div className={`form-group ${styles.grupoSinMargen}`}>
        <label className="form-label">Opciones de respuesta *</label>
        {options.length > 0 && (
          <div className={styles.listaOpciones}>
            {options.map((opt, i) => (
              <div key={i} className={styles.opcion}>
                <span className={styles.opcionNumero}>{i + 1}</span>
                <span className={styles.opcionTexto}>{opt}</span>
                <button type="button" onClick={() => remove(i)}
                  className={styles.opcionQuitar}>
                  <i className="fas fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className={styles.filaAgregar}>
          <input type="text" className={`form-control ${styles.campoOpcion}`}
            placeholder="Escribe una opción y presiona + o Enter"
            value={newOpt} onChange={e => setNewOpt(e.target.value)} onKeyDown={handleKey} />
          <button type="button" onClick={add} disabled={!newOpt.trim()}
            className={newOpt.trim() ? styles.botonMasActivo : styles.botonMas}>+</button>
        </div>
        {options.length < 2 && (
          <div className={styles.nota}>Agrega al menos 2 opciones</div>
        )}
      </div>
      {isMultiple && options.length >= 2 && (
        <div className={`form-group ${styles.grupoSinMargen}`}>
          <label className="form-label">¿Cuántas opciones puede seleccionar el votante?</label>
          <div className={styles.cuantas}>
            {options.map((_, i) => i + 1).map(n => (
              <button key={n} type="button"
                onClick={() => onMaxChoicesChange(String(n))}
                className={`btn btn-sm ${String(maxChoices) === String(n) ? 'btn-primary' : 'btn-secondary'}`}>
                {n === options.length ? `Todas (${n})` : n}
              </button>
            ))}
          </div>
          <div className={styles.notaSeis}>
            {maxChoices
              ? <>El votante podrá marcar hasta <strong>{maxChoices}</strong> opción{maxChoices > 1 ? 'es' : ''}</>
              : 'Selecciona cuántas opciones puede marcar cada votante'}
          </div>
        </div>
      )}
    </div>
  );
}

// F-17 (b): tarjetas del tipo de decisión (obligatorio en sí/no; ninguna viene
// marcada para que el staff lo piense). En conjuntos no residenciales no
// existe "No económica".
function TipoDecision({ valor, onChange, esResidencial, faltante }) {
  const opciones = DECISIONES.filter(d => !d.soloResidencial || esResidencial);
  return (
    <div className="form-group">
      <label className="form-label">¿Qué tipo de decisión es? *</label>
      <div className={styles.tarjetas}>
        {opciones.map(d => {
          const sel = valor === d.valor;
          return (
            <button type="button" key={d.valor} onClick={() => onChange(d.valor)} aria-pressed={sel}
              className={sel ? styles.tarjetaElegida : faltante ? styles.tarjetaFalta : styles.tarjeta}>
              <div className={styles.tarjetaTitulo}>{d.icono} {d.titulo}</div>
              <div className={styles.tarjetaEjemplos}>{d.ejemplos}</div>
              <div className={sel ? styles.tarjetaReglaElegida : styles.tarjetaRegla}>{d.regla}</div>
            </button>
          );
        })}
      </div>
      {faltante && <div className={styles.avisoFalta}>Elige qué tipo de decisión es: define con qué regla se aprueba.</div>}
    </div>
  );
}

export default function QuestionFormModal({ editing, form, set, assemblies, save, close, faltaDecision, error }) {
  const asm = assemblies.find(a => String(a.id) === String(form.assembly_id));
  const esResidencial = (asm?.property?.tipo_propiedad || 'residencial') === 'residencial';
  // El aviso va al final del cuerpo, que es lo que se desplaza: en un celular quedaba fuera de la vista y parecía que «Guardar»
  // no había hecho nada. `block: 'nearest'` mueve solo lo necesario dentro del modal (no la página). `intentos` hace que un
  // segundo «Guardar» con el MISMO error vuelva a traerlo a la vista aunque el texto no cambie.
  const errorRef = useRef(null);
  const [intentos, setIntentos] = useState(0);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ block: 'nearest' }); }, [error, intentos]);
  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{editing ? 'Editar' : 'Nueva'} pregunta</h3>
          <button className="btn-close" onClick={close}>✕</button>
        </div>
        <form onSubmit={(e) => { setIntentos(n => n + 1); save(e); }} noValidate>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Asamblea *</label>
              <SearchableSelect value={form.assembly_id} onChange={(id) => set('assembly_id', id)} placeholder="Seleccione..." allowEmpty={false} minWidth={0} maxWidth={9999} ariaLabel="Asamblea"
                options={assemblies.map(a => ({ value: a.id, label: `#${a.number_assembly}${a.assembly_type ? ` · ${a.assembly_type}` : ''}${a.property?.name ? ` — ${a.property.name}` : ''}` }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Pregunta *</label>
              <textarea className="form-control" rows="3" value={form.question_text}
                onChange={e => set('question_text', e.target.value)} required
                maxLength={MAX_QUESTION_TEXT}
                placeholder="Escribe la pregunta para la votación..." />
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de pregunta</label>
              <SearchableSelect value={form.question_type} onChange={(v) => set('question_type', v)} minWidth={0} maxWidth={9999} allowEmpty={false} ariaLabel="Tipo de pregunta" options={[{ value: 'yes_no', label: 'Sí / No' }, { value: 'multiple_choice', label: 'Múltiple opción' }, { value: 'one_choice', label: 'Una opción' }]} />
            </div>
            {form.question_type === 'yes_no' && (
              <TipoDecision valor={form.tipo_decision} onChange={v => set('tipo_decision', v)}
                esResidencial={esResidencial} faltante={faltaDecision && !form.tipo_decision} />
            )}
            {(form.question_type === 'multiple_choice' || form.question_type === 'one_choice') && (
              <OptionsEditor
                options={parseOptions(form.options)}
                onChange={arr => set('options', serializeOptions(arr))}
                maxChoices={form.max_choices}
                onMaxChoicesChange={v => set('max_choices', v)}
                isMultiple={form.question_type === 'multiple_choice'}
              />
            )}
            {error && <div ref={errorRef} className={`alert alert-danger ${styles.avisoError}`} role="alert">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={close}>Cancelar</button>
            <button type="submit" className="btn btn-primary"><i className="fas fa-save" /> Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}
